import { ServiceLine } from 'api/types/service.types';
import DropoffAtDoorLandscapeJPG from 'assets/images/dropoff/dropoff-at-door-landscape.jpg';
import DropoffAtDoorLandscape from 'assets/images/dropoff/dropoff-at-door-landscape.webp';
import DropoffAtDoorPortraitActiveJPG from 'assets/images/dropoff/dropoff-at-door-portrait.jpg';
import DropoffAtDoorPortraitActive from 'assets/images/dropoff/dropoff-at-door-portrait.webp';
import DropoffAtDoorShoePortraitActiveJPG from 'assets/images/dropoff/shoe-delivery-at-door-active.png';
import DropoffAtDoorShoePortraitActive from 'assets/images/dropoff/shoe-delivery-at-door-active.webp';
import MeetDriverLandscapeJPG from 'assets/images/meet-driver/meet-driver-landscape.png';
import MeetDriverLandscape from 'assets/images/meet-driver/meet-driver-landscape.webp';
import MeetDriverPortraitActiveJPG from 'assets/images/meet-driver/meet-driver-portrait.png';
import MeetDriverPortraitActive from 'assets/images/meet-driver/meet-driver-portrait.webp';
import PickupAtDoorLandscapeJPG from 'assets/images/pickup/pickup-at-door-landscape.jpg';
import PickupAtDoorLandscape from 'assets/images/pickup/pickup-at-door-landscape.webp';
import PickupAtDoorPortraitActiveJPG from 'assets/images/pickup/pickup-at-door-portrait.jpg';
import PickupAtDoorPortraitActive from 'assets/images/pickup/pickup-at-door-portrait.webp';
import cx from 'classnames';
import Button from 'components/atoms/Button';
import BottomSheet from 'components/molecules/BottomSheet';
import { BUTTON_NAMES } from 'constant/button-names';
import useImagePreloader from 'hooks/useImagePreloader';
import type { CollectionMethods } from 'packages/shared/newOrder/types';
import React, { useMemo } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import styles from './styles.module.scss';

const IMAGES = {
  PICKUP: {
    AT_DOOR: {
      OPTION: PickupAtDoorPortraitActive,
      OPTION_FALLBACK: PickupAtDoorPortraitActiveJPG,
      DEFAULTED: PickupAtDoorLandscape,
      DEFAULTED_FALLBACK: PickupAtDoorLandscapeJPG,
    },
    IN_PERSON: {
      OPTION: MeetDriverPortraitActive,
      OPTION_FALLBACK: MeetDriverPortraitActiveJPG,
      DEFAULTED: MeetDriverLandscape,
      DEFAULTED_FALLBACK: MeetDriverLandscapeJPG,
    },
  },
  DROPOFF: {
    AT_DOOR: {
      OPTION: DropoffAtDoorPortraitActive,
      OPTION_FALLBACK: DropoffAtDoorPortraitActiveJPG,
      DEFAULTED: DropoffAtDoorLandscape,
      DEFAULTED_FALLBACK: DropoffAtDoorLandscapeJPG,
    },
    IN_PERSON: {
      OPTION: MeetDriverPortraitActive,
      OPTION_FALLBACK: MeetDriverPortraitActiveJPG,
      DEFAULTED: MeetDriverLandscape,
      DEFAULTED_FALLBACK: MeetDriverLandscapeJPG,
    },
    SHOECARE: {
      OPTION: DropoffAtDoorShoePortraitActive,
      OPTION_FALLBACK: DropoffAtDoorShoePortraitActiveJPG,
    },
  },
};

interface CollectionMethodBoxProps {
  isSelected: boolean;
  onSelected?: () => any;
  collectionMethod: 'AT_DOOR' | 'IN_PERSON';
  action: 'PICKUP' | 'DROPOFF';
  serviceLine?: ServiceLine;
}

export interface ICollectionMethodSelectorProps {
  show?: boolean;
  onClose?: (type?: string) => any;
  collectionMethod: 'AT_DOOR' | 'IN_PERSON';
  action: 'PICKUP' | 'DROPOFF';
  allowChange: boolean;
  description?: string;
  atDoorDescription?: string;
  inPersonDescription?: string;
  isLoading?: boolean;
  serviceLine?: ServiceLine;
  isShowTitle?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  onMethodSelectionChanged?: (method: CollectionMethods) => void;
}

const StyledContainer = styled.div`
  background: ${({ theme }) => theme.components.collectionMethodSelector.unselected.backgroundColor};
  color: ${({ theme }) => theme.components.collectionMethodSelector.unselected.color};
  border: ${({ theme }) =>
    `${theme.components.collectionMethodSelector.unselected.border.width}px solid ${theme.components.collectionMethodSelector.unselected.border.color}`};
  border-radius: ${({ theme }) => theme.components.collectionMethodSelector.unselected.border.radius}px;
  &.selected {
    background: ${({ theme }) => theme.components.collectionMethodSelector.selected.backgroundColor};
    border-color: ${({ theme }) => theme.components.collectionMethodSelector.selected.border.color};
  }
`;

const StyledH4 = styled.h4`
  font-weight: 500;
  font-size: 14px;
  margin: 0px;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
  &.selected {
    color: ${({ theme }) => theme.components.collectionMethodSelector.selected.color};
    background-color: ${({ theme }) => theme.components.collectionMethodSelector.title.backgroundColor.selected};
  }
`;

const CollectionMethodBox: React.FC<CollectionMethodBoxProps> = ({
  isSelected = false,
  onSelected = () => {},
  collectionMethod = 'AT_DOOR',
  action = 'PICKUP',
  serviceLine,
}) => {
  const handleSelected = () => {
    onSelected();
  };
  const { t } = useTranslation();
  const atDoorTitle = action === 'PICKUP' ? t('common.collectAtDoor') : t('common.deliveryAtDoor');
  const inPersonTitle = action === 'PICKUP' ? t('common.collectInPerson') : t('common.deliveryInPerson');
  const title = collectionMethod === 'AT_DOOR' ? atDoorTitle : inPersonTitle;

  useImagePreloader([
    PickupAtDoorPortraitActive,
    DropoffAtDoorPortraitActive,
    MeetDriverPortraitActive,
    PickupAtDoorLandscape,
    MeetDriverLandscape,
    DropoffAtDoorLandscape,
  ]);

  const shouldUseShoeCareDropoffOption = serviceLine === ServiceLine.SHOE_SERVICE && action === 'DROPOFF' && collectionMethod === 'AT_DOOR';

  const src = useMemo(
    () => (shouldUseShoeCareDropoffOption ? IMAGES.DROPOFF.SHOECARE.OPTION : IMAGES[action][collectionMethod].OPTION),
    [action, collectionMethod, shouldUseShoeCareDropoffOption]
  );

  const srcFallback = useMemo(
    () => (shouldUseShoeCareDropoffOption ? IMAGES.DROPOFF.SHOECARE.OPTION_FALLBACK : IMAGES[action][collectionMethod].OPTION_FALLBACK),
    [action, collectionMethod, shouldUseShoeCareDropoffOption]
  );

  return (
    <StyledContainer
      className={cx(styles.collectionMethodBox, {
        [styles.selected]: isSelected,
        selected: isSelected,
      })}
      onClick={handleSelected}
    >
      <picture>
        <source srcSet={src} type="image/webp" />
        <source srcSet={srcFallback} type="image/jpeg" />
        <img src={src} alt={collectionMethod} className={cx(styles.image)} />
      </picture>
      <StyledH4 className={cx('d-flex p-2 align-items-center justify-content-center text-center', styles.title, { selected: isSelected })}>
        {title}
      </StyledH4>
    </StyledContainer>
  );
};

const StyledDesctiptionText = styled.h4`
  font-weight: normal;
  color: ${({ theme }) => theme.components.collectionMethodSelector.description.color};
`;

const CollectionMethodSelector: React.FC<ICollectionMethodSelectorProps> = ({
  show = false,
  onClose = () => {},
  collectionMethod = 'AT_DOOR',
  action = 'PICKUP',
  allowChange = true,
  description,
  atDoorDescription,
  inPersonDescription,
  isLoading = false,
  serviceLine,
  isShowTitle,
}) => {
  const [selectedCollectionMethod, setCollectionMethod] = React.useState(collectionMethod);
  const { t } = useTranslation();
  const theme = useTheme();

  React.useEffect(() => {
    setCollectionMethod(collectionMethod);
  }, [collectionMethod]);

  const descriptionText = React.useMemo(
    () => (!allowChange ? description : selectedCollectionMethod === 'AT_DOOR' ? atDoorDescription : inPersonDescription),
    [allowChange, description, selectedCollectionMethod, atDoorDescription, inPersonDescription]
  );

  const handleSelect = () => {
    onClose(selectedCollectionMethod);
  };

  const SheetTitle = useMemo(
    () => (isShowTitle ? t(`newOrderPage.deliveryMethodHeading.${collectionMethod}.${serviceLine}`) : ''),
    [collectionMethod, isShowTitle, serviceLine, t]
  );

  return (
    <BottomSheet isCapitalizeHeaderText isNoWrapTitle={isShowTitle} title={SheetTitle} show={show} onClose={onClose}>
      <div className={cx('mx-8')}>
        {/* {allowChange && ( */}
        <Row>
          <Col xs={6} className="padding-start-0 padding-end-2">
            <CollectionMethodBox
              collectionMethod="AT_DOOR"
              isSelected={selectedCollectionMethod === 'AT_DOOR'}
              action={action}
              onSelected={() => setCollectionMethod('AT_DOOR')}
              serviceLine={serviceLine}
            />
          </Col>
          <Col xs={6} className="padding-end-0 padding-start-2">
            <CollectionMethodBox
              collectionMethod="IN_PERSON"
              isSelected={selectedCollectionMethod === 'IN_PERSON'}
              action={action}
              onSelected={() => setCollectionMethod('IN_PERSON')}
              serviceLine={serviceLine}
            />
          </Col>
        </Row>
        {/* )} */}
        {/* {!allowChange && (
                    <Row className="d-flex justify-content-center">
                        <img
                            src={IMAGES[action][collectionMethod]['DEFAULTED']}
                            alt={`${action}.${collectionMethod}`}
                        />
                    </Row>
                )} */}
        <Row className="d-flex justify-content-center">
          <StyledDesctiptionText className={cx('text-center my-8')}>{descriptionText}</StyledDesctiptionText>
        </Row>
        <Row>
          <Button
            textColor={theme.components.pages.pickupDropoff?.button?.textColor}
            backgroundColor={theme.components.pages.pickupDropoff?.button?.background}
            block
            size="lg"
            onClick={handleSelect}
            isLoading={isLoading}
            buttonName={BUTTON_NAMES.CONTINUE}
          >
            {t('common.continue')}
          </Button>
        </Row>
      </div>
    </BottomSheet>
  );
};

export default CollectionMethodSelector;
